import AddRounded from '@mui/icons-material/AddRounded';
import CloudUploadRounded from '@mui/icons-material/CloudUploadRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import SyncRounded from '@mui/icons-material/SyncRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { DataTable } from '../../components/DataTable';
import { VariantPicker, type Variant } from '../../components/pickers';
import { dateTime, money } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import {
  useFoodpandaMenu,
  useMenuActions,
  type FoodpandaMenu,
  type FoodpandaMenuItem,
  type FoodpandaStatus,
} from './api';

const MENU_STATUS: Record<
  FoodpandaMenu['status'],
  { label: string; color: 'default' | 'info' | 'success' | 'error' }
> = {
  NEVER: { label: 'Not uploaded yet', color: 'default' },
  PENDING: { label: 'Foodpanda is importing it', color: 'info' },
  DONE: { label: 'Up to date on Foodpanda', color: 'success' },
  FAILED: { label: 'Upload failed', color: 'error' },
};

/**
 * What Foodpanda sells: managers pick the products (their SKU is the Foodpanda item code), set a
 * Foodpanda price and upload the menu; anyone handling orders can mark an item sold out at an
 * outlet. Items also go off by themselves when stock (or a recipe ingredient) runs out.
 */
export function MenuTab({ status, canManage }: { status: FoodpandaStatus; canManage: boolean }) {
  const { format } = useShop();
  const notify = useNotify();
  const menu = useFoodpandaMenu();
  const actions = useMenuActions();
  const outlets = status.outlets.filter((o) => o.isActive);
  const [outletId, setOutletId] = useState(outlets[0]?.id ?? '');
  const [adding, setAdding] = useState(false);
  const [pricing, setPricing] = useState<FoodpandaMenuItem | null>(null);
  const [removing, setRemoving] = useState<FoodpandaMenuItem | null>(null);
  const outlet = outlets.find((o) => o.id === outletId) ?? outlets[0];

  if (menu.isLoading) return <CircularProgress />;
  if (!menu.data) return <Alert severity="error">The menu could not be loaded.</Alert>;
  const data = menu.data;
  const state = MENU_STATUS[data.status];
  const at = (item: FoodpandaMenuItem) => item.outlets.find((o) => o.outletId === outlet?.id);

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ alignItems: { sm: 'center' }, flexWrap: 'wrap' }}
      >
        <Chip label={state.label} color={state.color} variant="outlined" />
        {data.pushedAt && (
          <Typography variant="body2" color="text.secondary">
            Last upload {dateTime(data.pushedAt, format)}
            {data.message ? ` · ${data.message}` : ''}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {outlets.length > 1 && (
          <TextField
            select
            size="small"
            label="Outlet"
            value={outlet?.id ?? ''}
            onChange={(e) => setOutletId(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            {outlets.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        {outlet && (
          <Button
            startIcon={<SyncRounded />}
            disabled={actions.sync.isPending}
            onClick={() =>
              actions.sync.mutateAsync(outlet.id).then((r) => {
                const failed = r.outlets.find((o) => o.error);
                if (failed?.error) notify.error(new Error(failed.error));
                else notify.success('Availability sent to Foodpanda');
              }, notify.error)
            }
          >
            Send availability now
          </Button>
        )}
        {canManage && (
          <>
            <Button variant="outlined" startIcon={<AddRounded />} onClick={() => setAdding(true)}>
              Add products
            </Button>
            <Button
              variant="contained"
              startIcon={<CloudUploadRounded />}
              disabled={actions.push.isPending || data.items.length === 0}
              onClick={() =>
                actions.push
                  .mutateAsync()
                  .then(() => notify.success('Menu sent to Foodpanda'), notify.error)
              }
            >
              Upload menu
            </Button>
          </>
        )}
      </Stack>
      {data.status === 'FAILED' && data.message && (
        <Alert severity="error">Foodpanda could not import the menu: {data.message}</Alert>
      )}
      {!data.autoAvailability && (
        <Alert severity="info">
          Items stay on Foodpanda whatever the stock (automatic availability is off in the set-up).
          Use the switches to mark them sold out.
        </Alert>
      )}
      <DataTable
        rows={data.items}
        rowKey={(i) => i.id}
        empty={
          canManage
            ? 'Nothing on the Foodpanda menu yet. Add the products you sell on Foodpanda.'
            : 'Nothing on the Foodpanda menu yet.'
        }
        columns={[
          {
            key: 'item',
            header: 'Item',
            render: (i) => (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {i.variant.name}
                  {i.productType === 'COMBO' && (
                    <Chip size="small" label="Deal" sx={{ ml: 1, height: 20 }} />
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {i.variant.sku}
                  {i.category ? ` · ${i.category.name}` : ''}
                </Typography>
                {i.problem && (
                  <Typography variant="caption" color="error" component="div">
                    {i.problem}
                  </Typography>
                )}
              </Box>
            ),
          },
          {
            key: 'price',
            header: 'Foodpanda price',
            align: 'right',
            render: (i) => (
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ alignItems: 'center', justifyContent: 'flex-end' }}
              >
                <Box>
                  <Typography variant="body2">{money(i.effectivePrice, format)}</Typography>
                  {i.price !== null && (
                    <Typography variant="caption" color="text.secondary">
                      shop {money(i.salePrice, format)}
                    </Typography>
                  )}
                </Box>
                {canManage && (
                  <IconButton
                    size="small"
                    aria-label={`Foodpanda price of ${i.variant.name}`}
                    onClick={() => setPricing(i)}
                  >
                    <EditRounded fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            ),
          },
          {
            key: 'stock',
            header: 'Stock',
            hideBelow: 'sm',
            render: (i) =>
              at(i)?.inStock ? (
                <Chip size="small" label="In stock" color="success" variant="outlined" />
              ) : (
                <Chip size="small" label="Out of stock" color="warning" variant="outlined" />
              ),
          },
          {
            key: 'on',
            header: 'On Foodpanda',
            render: (i) => {
              const s = at(i);
              if (!s || !outlet) return '—';
              return (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Switch
                    checked={!s.manualOff}
                    disabled={actions.setAvailable.isPending}
                    slotProps={{ input: { 'aria-label': `Sell ${i.variant.name} on Foodpanda` } }}
                    onChange={(e) =>
                      actions.setAvailable
                        .mutateAsync({
                          outletId: outlet.id,
                          itemId: i.id,
                          available: e.target.checked,
                        })
                        .then(
                          () =>
                            notify.success(
                              e.target.checked
                                ? `${i.variant.name} back on Foodpanda`
                                : `${i.variant.name} marked sold out`,
                            ),
                          notify.error,
                        )
                    }
                  />
                  <Typography
                    variant="caption"
                    color={s.available ? 'success.main' : 'text.secondary'}
                  >
                    {s.available ? 'Showing' : s.manualOff ? 'Sold out' : 'Off: no stock'}
                  </Typography>
                </Stack>
              );
            },
          },
          ...(canManage
            ? [
                {
                  key: 'remove',
                  header: '',
                  align: 'right' as const,
                  render: (i: FoodpandaMenuItem) => (
                    <Tooltip title="Take off the Foodpanda menu">
                      <IconButton
                        size="small"
                        aria-label={`Remove ${i.variant.name}`}
                        onClick={() => setRemoving(i)}
                      >
                        <DeleteOutlineRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                },
              ]
            : []),
        ]}
      />
      {adding && (
        <AddProductsDialog
          onClose={() => setAdding(false)}
          onAdd={(ids) =>
            actions.add.mutateAsync(ids).then(() => {
              notify.success('Added to the Foodpanda menu. Upload the menu to send it.');
              setAdding(false);
            }, notify.error)
          }
        />
      )}
      {pricing && (
        <PriceDialog
          item={pricing}
          onClose={() => setPricing(null)}
          onSave={(price) =>
            actions.price
              .mutateAsync({ id: pricing.id, version: pricing.version, price })
              .then(() => {
                notify.success('Price saved. Upload the menu to send it.');
                setPricing(null);
              }, notify.error)
          }
        />
      )}
      <ConfirmDialog
        open={!!removing}
        title="Take off the Foodpanda menu?"
        body={`${removing?.variant.name ?? ''} stays in your products; upload the menu to remove it from Foodpanda.`}
        confirmLabel="Remove"
        danger
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          actions.remove.mutateAsync(removing?.id ?? '').then(() => setRemoving(null), notify.error)
        }
      />
    </Stack>
  );
}

function AddProductsDialog({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: (variantIds: string[]) => Promise<unknown>;
}) {
  const [picked, setPicked] = useState<{ id: string; label: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const pick = (v: Variant | null) => {
    if (!v) return;
    const r = v as unknown as { id: string; sku?: string; displayName?: string; name?: string };
    const label = r.displayName ?? r.name ?? r.sku ?? r.id;
    setPicked((p) => (p.some((x) => x.id === r.id) ? p : [...p, { id: r.id, label }]));
  };
  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add products to the Foodpanda menu</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Each product’s SKU is its Foodpanda item code. Deals are sent with their choices.
          </Typography>
          <VariantPicker key={picked.length} value={null} onChange={pick} label="Find a product" />
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
            {picked.map((p) => (
              <Chip
                key={p.id}
                label={p.label}
                onDelete={() => setPicked((all) => all.filter((x) => x.id !== p.id))}
              />
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy || picked.length === 0}
          onClick={() => {
            setBusy(true);
            void onAdd(picked.map((p) => p.id)).finally(() => setBusy(false));
          }}
        >
          Add {picked.length || ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PriceDialog({
  item,
  onClose,
  onSave,
}: {
  item: FoodpandaMenuItem;
  onClose: () => void;
  onSave: (price: string | null) => Promise<unknown>;
}) {
  const { format } = useShop();
  const [value, setValue] = useState(item.price ?? '');
  const [busy, setBusy] = useState(false);
  const ok = value === '' || /^\d{1,15}(\.\d{1,4})?$/.test(value);
  const save = (price: string | null) => {
    setBusy(true);
    void onSave(price).finally(() => setBusy(false));
  };
  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Foodpanda price · {item.variant.name}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Price on Foodpanda"
          value={value}
          onChange={(e) => setValue(e.target.value.replace(/[^\d.]/g, ''))}
          error={!ok}
          helperText={`Empty = the shop price (${money(item.salePrice, format)})`}
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" disabled={busy || !ok} onClick={() => save(value || null)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
